/**
 * PortalAnalytics -- Revenue-routing reporting view
 *
 * Two tabs:
 *   "Scoring Performance" -- real data from session_events table
 *   "Leads"               -- seeded test data from leads table
 *
 * Routes:
 *   /portal/analytics            -> aggregate across all chatbots
 *   /portal/analytics/:id        -> single chatbot
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { format, subDays, startOfDay } from 'date-fns';
import PortalLayout from '@/components/portal/PortalLayout';
import { supabaseGet } from '@/utils/supabaseRest';
import { Button } from '@/components/ui/button';
import {
  TrendingUp, TrendingDown, DollarSign, Users,
  ArrowLeft, Download, Target, Zap, BarChart3, Activity,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// ── Types ────────────────────────────────────────────

type TimeRange = '7' | '30' | '90';
type ActiveTab = 'scoring' | 'leads';

interface SessionEvent {
  id: string;
  session_id: string;
  event_type: string;       // 'presented' | 'rejected' | 'converted' | 'session_end'
  vertical_id: string | null;
  phase: number | null;
  score: number | null;
  revenue: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface Lead {
  id: string;
  chatbot_id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  source: string | null;
  created_at: string;
  data: Record<string, unknown>;
}

interface DeliveryLog {
  id: string;
  chatbot_id: string;
  destination_type: string | null;
  success: boolean;
  created_at: string;
}

// ── Helpers ──────────────────────────────────────────

function leadRevenue(lead: Lead): number {
  switch (lead.status) {
    case 'converted': return 35;
    case 'contacted': return 18;
    case 'qualified': return 10;
    case 'new':
    default:          return 5;
  }
}

function pct(change: number): string {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}%`;
}

function calcChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

const SOURCE_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];
const PHASE_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b'];

// ── Component ────────────────────────────────────────

export default function PortalAnalytics() {
  const { id: chatbotIdParam } = useParams<{ id?: string }>();
  const { user } = useAuth();
  const userId = user?.id;

  const [activeTab, setActiveTab] = useState<ActiveTab>('scoring');
  const [timeRange, setTimeRange] = useState<TimeRange>('30');

  // Scoring data
  const [events, setEvents] = useState<SessionEvent[]>([]);

  // Leads data
  const [leads, setLeads] = useState<Lead[]>([]);
  const [deliveryLogs, setDeliveryLogs] = useState<DeliveryLog[]>([]);

  const [chatbotName, setChatbotName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // ── Data load ──
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const days = parseInt(timeRange);
        const since = subDays(new Date(), days * 2).toISOString();

        // Session events query
        const eventsPath = `session_events?select=id,session_id,event_type,vertical_id,phase,score,revenue,metadata,created_at&created_at=gte.${encodeURIComponent(since)}&order=created_at.desc`;

        // Leads query (optionally scoped to one chatbot)
        let leadPath = `leads?select=id,chatbot_id,name,email,phone,status,source,created_at,data&created_at=gte.${encodeURIComponent(since)}&order=created_at.desc`;
        if (chatbotIdParam) leadPath += `&chatbot_id=eq.${chatbotIdParam}`;

        let deliveryPath = `delivery_logs?select=id,chatbot_id,destination_type,success,created_at&created_at=gte.${encodeURIComponent(since)}`;
        if (chatbotIdParam) deliveryPath += `&chatbot_id=eq.${chatbotIdParam}`;

        const [eventsData, leadsData, deliveryData, chatbotRows] = await Promise.all([
          supabaseGet<SessionEvent[]>(eventsPath),
          supabaseGet<Lead[]>(leadPath),
          supabaseGet<DeliveryLog[]>(deliveryPath),
          chatbotIdParam
            ? supabaseGet<{ name: string }[]>(`chatbots?select=name&id=eq.${chatbotIdParam}`)
            : Promise.resolve([] as { name: string }[]),
        ]);

        if (cancelled) return;

        setEvents(eventsData || []);
        setLeads(leadsData || []);
        setDeliveryLogs(deliveryData || []);
        setChatbotName(chatbotRows[0]?.name || '');
      } catch (err) {
        console.warn('Analytics: load failed', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId, chatbotIdParam, timeRange]);

  // ── Period splits ──
  const days = parseInt(timeRange);
  const cutoff = subDays(new Date(), days);

  const currentEvents = useMemo(
    () => events.filter(e => new Date(e.created_at) >= cutoff),
    [events, cutoff],
  );
  const previousEvents = useMemo(
    () => events.filter(e => new Date(e.created_at) < cutoff),
    [events, cutoff],
  );

  const currentLeads = useMemo(
    () => leads.filter(l => new Date(l.created_at) >= cutoff),
    [leads, cutoff],
  );
  const previousLeads = useMemo(
    () => leads.filter(l => new Date(l.created_at) < cutoff),
    [leads, cutoff],
  );

  // ════════════════════════════════════════════════════
  //  SCORING KPIs
  // ════════════════════════════════════════════════════

  const scoringKpis = useMemo(() => {
    const calc = (evts: SessionEvent[]) => {
      const presented = evts.filter(e => e.event_type === 'presented');
      const converted = evts.filter(e => e.event_type === 'converted');
      const rejected  = evts.filter(e => e.event_type === 'rejected');
      const uniqueSessions = new Set(evts.map(e => e.session_id)).size;
      const totalRevenue = converted.reduce((s, e) => s + (e.revenue || 0), 0);

      const rpus = uniqueSessions > 0 ? totalRevenue / uniqueSessions : 0;
      const cvr = presented.length > 0 ? (converted.length / presented.length) * 100 : 0;
      const avgVerticals = uniqueSessions > 0 ? presented.length / uniqueSessions : 0;
      const rejectionRate = (rejected.length + converted.length) > 0
        ? (rejected.length / (rejected.length + converted.length)) * 100
        : 0;

      return { rpus, cvr, avgVerticals, rejectionRate, totalRevenue, uniqueSessions, presentedCount: presented.length, convertedCount: converted.length };
    };

    const curr = calc(currentEvents);
    const prev = calc(previousEvents);

    return {
      rpus: curr.rpus,
      rpusChange: calcChange(curr.rpus, prev.rpus),
      cvr: curr.cvr,
      cvrChange: calcChange(curr.cvr, prev.cvr),
      avgVerticals: curr.avgVerticals,
      avgVerticalsChange: calcChange(curr.avgVerticals, prev.avgVerticals),
      rejectionRate: curr.rejectionRate,
      rejectionRateChange: calcChange(curr.rejectionRate, prev.rejectionRate),
      totalRevenue: curr.totalRevenue,
      uniqueSessions: curr.uniqueSessions,
    };
  }, [currentEvents, previousEvents]);

  // ── Vertical performance (horizontal bar) ──
  const verticalPerformance = useMemo(() => {
    const map = new Map<string, { presented: number; converted: number }>();
    for (const e of currentEvents) {
      if (e.event_type !== 'presented' && e.event_type !== 'converted') continue;
      const vid = e.vertical_id || 'unknown';
      if (!map.has(vid)) map.set(vid, { presented: 0, converted: 0 });
      const entry = map.get(vid)!;
      if (e.event_type === 'presented') entry.presented++;
      if (e.event_type === 'converted') entry.converted++;
    }
    return Array.from(map.entries())
      .map(([vertical_id, stats]) => ({
        vertical_id,
        presented: stats.presented,
        converted: stats.converted,
        conversionRate: stats.presented > 0 ? ((stats.converted / stats.presented) * 100) : 0,
      }))
      .filter(v => v.presented > 0)
      .sort((a, b) => b.presented - a.presented);
  }, [currentEvents]);

  // ── Phase distribution (pie) ──
  const phaseDistribution = useMemo(() => {
    const counts = new Map<number, number>();
    for (const e of currentEvents) {
      if (e.phase == null) continue;
      if (e.event_type !== 'presented' && e.event_type !== 'converted' && e.event_type !== 'rejected') continue;
      counts.set(e.phase, (counts.get(e.phase) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([phase, count]) => ({ name: `Phase ${phase}`, value: count, phase }))
      .sort((a, b) => a.phase - b.phase);
  }, [currentEvents]);

  // ── Scoring time series (area chart) ──
  const scoringTimeSeries = useMemo(() => {
    const buckets = Array.from({ length: days }, (_, i) => {
      const date = startOfDay(subDays(new Date(), days - 1 - i));
      return { date, label: format(date, 'MMM d'), events: 0, revenue: 0 };
    });
    for (const e of currentEvents) {
      const d = startOfDay(new Date(e.created_at)).getTime();
      const bucket = buckets.find(b => b.date.getTime() === d);
      if (bucket) {
        bucket.events++;
        if (e.event_type === 'converted') bucket.revenue += (e.revenue || 0);
      }
    }
    return buckets;
  }, [currentEvents, days]);

  // ════════════════════════════════════════════════════
  //  LEADS KPIs (existing logic preserved)
  // ════════════════════════════════════════════════════

  const leadsKpis = useMemo(() => {
    const currentRevenue = currentLeads.reduce((s, l) => s + leadRevenue(l), 0);
    const previousRevenue = previousLeads.reduce((s, l) => s + leadRevenue(l), 0);
    const currentSessions = currentLeads.length;
    const previousSessions = previousLeads.length;
    const currentRpus = currentSessions > 0 ? currentRevenue / currentSessions : 0;
    const previousRpus = previousSessions > 0 ? previousRevenue / previousSessions : 0;
    const currentConverted = currentLeads.filter(l => l.status === 'converted' || l.status === 'contacted').length;
    const previousConverted = previousLeads.filter(l => l.status === 'converted' || l.status === 'contacted').length;
    const currentCvr = currentSessions > 0 ? (currentConverted / currentSessions) * 100 : 0;
    const previousCvr = previousSessions > 0 ? (previousConverted / previousSessions) * 100 : 0;
    return {
      rpus: currentRpus, rpusChange: calcChange(currentRpus, previousRpus),
      revenue: currentRevenue, revenueChange: calcChange(currentRevenue, previousRevenue),
      sessions: currentSessions, sessionsChange: calcChange(currentSessions, previousSessions),
      cvr: currentCvr, cvrChange: calcChange(currentCvr, previousCvr),
    };
  }, [currentLeads, previousLeads]);

  const leadsTimeSeries = useMemo(() => {
    const buckets = Array.from({ length: days }, (_, i) => {
      const date = startOfDay(subDays(new Date(), days - 1 - i));
      return { date, label: format(date, 'MMM d'), leads: 0, revenue: 0 };
    });
    for (const lead of currentLeads) {
      const d = startOfDay(new Date(lead.created_at)).getTime();
      const bucket = buckets.find(b => b.date.getTime() === d);
      if (bucket) { bucket.leads++; bucket.revenue += leadRevenue(lead); }
    }
    return buckets;
  }, [currentLeads, days]);

  const sourceBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const lead of currentLeads) {
      const key = lead.source || 'unknown';
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return Array.from(counts.entries()).map(([name, value]) => ({ name, value }));
  }, [currentLeads]);

  const statusBreakdown = useMemo(() => {
    const order = ['new', 'qualified', 'contacted', 'converted'];
    const counts = new Map<string, number>();
    for (const lead of currentLeads) counts.set(lead.status, (counts.get(lead.status) || 0) + 1);
    return order.map(s => ({ name: s, leads: counts.get(s) || 0 }));
  }, [currentLeads]);

  const deliveryStats = useMemo(() => {
    const recent = deliveryLogs.filter(d => new Date(d.created_at) >= cutoff);
    const success = recent.filter(d => d.success).length;
    return { total: recent.length, success, rate: recent.length > 0 ? (success / recent.length) * 100 : 0 };
  }, [deliveryLogs, cutoff]);

  // ── CSV export ──
  const exportCsv = () => {
    if (activeTab === 'scoring') {
      const header = ['created_at', 'session_id', 'event_type', 'vertical_id', 'phase', 'score', 'revenue'];
      const rows = currentEvents.map(e => [
        e.created_at, e.session_id, e.event_type, e.vertical_id || '', String(e.phase ?? ''), String(e.score ?? ''), String(e.revenue ?? ''),
      ]);
      const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
      downloadCsv(csv, `scoring-events-${timeRange}d-${Date.now()}.csv`);
    } else {
      const header = ['created_at', 'name', 'email', 'phone', 'status', 'source', 'revenue'];
      const rows = currentLeads.map(l => [
        l.created_at, l.name || '', l.email || '', l.phone || '', l.status, l.source || '', leadRevenue(l).toFixed(2),
      ]);
      const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
      downloadCsv(csv, `leads-${timeRange}d-${Date.now()}.csv`);
    }
  };

  function downloadCsv(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Render ──
  return (
    <PortalLayout>
      <div className="min-h-screen bg-slate-950 text-white">
        {/* Header */}
        <div className="border-b border-slate-800 bg-slate-900/50 px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Link to="/portal" className="text-slate-400 hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-400" />
                  {chatbotName ? `${chatbotName} -- Analytics` : 'Revenue Analytics'}
                </h1>
                <p className="text-xs text-slate-500">
                  Scoring performance, RPUS, conversion lift, and routing data
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-lg border border-slate-700 bg-slate-800/50 p-0.5">
                {(['7', '30', '90'] as const).map((r) => (
                  <button
                    key={r}
                    onClick={() => setTimeRange(r)}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      timeRange === r ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {r}d
                  </button>
                ))}
              </div>
              <Button onClick={exportCsv} size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center gap-1 mt-4">
            <button
              onClick={() => setActiveTab('scoring')}
              className={`px-4 py-2 text-sm rounded-t-lg font-medium transition-colors ${
                activeTab === 'scoring'
                  ? 'bg-slate-800 text-emerald-400 border border-b-0 border-slate-700'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <Activity className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Scoring Performance
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              className={`px-4 py-2 text-sm rounded-t-lg font-medium transition-colors ${
                activeTab === 'leads'
                  ? 'bg-slate-800 text-emerald-400 border border-b-0 border-slate-700'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <Users className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Leads
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {activeTab === 'scoring' ? (
            <ScoringTab
              isLoading={isLoading}
              kpis={scoringKpis}
              timeSeries={scoringTimeSeries}
              verticalPerformance={verticalPerformance}
              phaseDistribution={phaseDistribution}
              currentEvents={currentEvents}
              timeRange={timeRange}
            />
          ) : (
            <LeadsTab
              isLoading={isLoading}
              kpis={leadsKpis}
              timeSeries={leadsTimeSeries}
              sourceBreakdown={sourceBreakdown}
              statusBreakdown={statusBreakdown}
              deliveryStats={deliveryStats}
              currentLeads={currentLeads}
              timeRange={timeRange}
            />
          )}
        </div>
      </div>
    </PortalLayout>
  );
}

// ════════════════════════════════════════════════════════
//  SCORING TAB
// ════════════════════════════════════════════════════════

interface ScoringTabProps {
  isLoading: boolean;
  kpis: {
    rpus: number; rpusChange: number;
    cvr: number; cvrChange: number;
    avgVerticals: number; avgVerticalsChange: number;
    rejectionRate: number; rejectionRateChange: number;
    totalRevenue: number; uniqueSessions: number;
  };
  timeSeries: { label: string; events: number; revenue: number }[];
  verticalPerformance: { vertical_id: string; presented: number; converted: number; conversionRate: number }[];
  phaseDistribution: { name: string; value: number; phase: number }[];
  currentEvents: SessionEvent[];
  timeRange: string;
}

function ScoringTab({ isLoading, kpis, timeSeries, verticalPerformance, phaseDistribution, currentEvents, timeRange }: ScoringTabProps) {
  return (
    <>
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Real RPUS"
          value={`$${kpis.rpus.toFixed(2)}`}
          change={kpis.rpusChange}
          icon={<DollarSign className="h-5 w-5" />}
          tint="emerald"
        />
        <KpiCard
          label="Conversion Rate"
          value={`${kpis.cvr.toFixed(1)}%`}
          change={kpis.cvrChange}
          icon={<Target className="h-5 w-5" />}
          tint="blue"
        />
        <KpiCard
          label="Avg Verticals / Session"
          value={kpis.avgVerticals.toFixed(1)}
          change={kpis.avgVerticalsChange}
          icon={<BarChart3 className="h-5 w-5" />}
          tint="violet"
        />
        <KpiCard
          label="Rejection Rate"
          value={`${kpis.rejectionRate.toFixed(1)}%`}
          change={kpis.rejectionRateChange}
          icon={<Zap className="h-5 w-5" />}
          tint="amber"
          invertChange
        />
      </div>

      {/* Revenue & Events area chart */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">Revenue & Events Over Time</h3>
            <p className="text-xs text-slate-500">Last {timeRange} days -- scoring events from session_events</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Total Revenue</div>
            <div className="text-lg font-bold text-emerald-400">${kpis.totalRevenue.toFixed(2)}</div>
            <div className="text-[10px] text-slate-500">{kpis.uniqueSessions} unique sessions</div>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timeSeries} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="scoringRevGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="scoringEvtGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue ($)" stroke="#10b981" fill="url(#scoringRevGradient)" strokeWidth={2} />
              <Area yAxisId="right" type="monotone" dataKey="events" name="Events" stroke="#3b82f6" fill="url(#scoringEvtGradient)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two-column: Vertical Performance + Phase Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Vertical Performance -- horizontal bar */}
        <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-slate-900/50 p-5">
          <h3 className="font-semibold mb-4">Vertical Performance</h3>
          {verticalPerformance.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-500 text-sm">No scoring data in this range</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={verticalPerformance} layout="vertical" margin={{ top: 4, right: 60, bottom: 4, left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="vertical_id"
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    width={75}
                  />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                    formatter={(value: number, name: string) => [value, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="presented" name="Presented" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="converted" name="Converted" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {/* Conversion rate labels below the chart */}
          {verticalPerformance.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3">
              {verticalPerformance.map(v => (
                <span key={v.vertical_id} className="text-xs text-slate-400">
                  <span className="text-slate-300 font-medium">{v.vertical_id}</span>{' '}
                  <span className="text-emerald-400">{v.conversionRate.toFixed(1)}% CVR</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Phase Distribution -- pie */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
          <h3 className="font-semibold mb-4">Phase Distribution</h3>
          {phaseDistribution.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-slate-500 text-sm">No phase data</div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={phaseDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={48}
                    dataKey="value"
                    label={(entry) => `${entry.name} (${entry.value})`}
                    labelLine={false}
                  >
                    {phaseDistribution.map((_, i) => (
                      <Cell key={i} fill={PHASE_COLORS[i % PHASE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="text-[10px] text-slate-500 mt-2 text-center">
            Early (1-2) vs late (3-4) conversion phases
          </p>
        </div>
      </div>

      {/* Recent scoring events table */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="font-semibold">Recent Scoring Events</h3>
          <span className="text-xs text-slate-500">{currentEvents.length} total -- showing last 15</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">Date</th>
                <th className="text-left px-5 py-2.5 font-medium">Session</th>
                <th className="text-left px-5 py-2.5 font-medium">Event</th>
                <th className="text-left px-5 py-2.5 font-medium">Vertical</th>
                <th className="text-center px-5 py-2.5 font-medium">Phase</th>
                <th className="text-right px-5 py-2.5 font-medium">Score</th>
                <th className="text-right px-5 py-2.5 font-medium">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500">Loading...</td></tr>
              ) : currentEvents.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500">No scoring events in this range</td></tr>
              ) : (
                currentEvents.slice(0, 15).map((evt) => (
                  <tr key={evt.id} className="border-t border-slate-800 hover:bg-slate-800/30">
                    <td className="px-5 py-2.5 text-slate-400 text-xs">{format(new Date(evt.created_at), 'MMM d, HH:mm')}</td>
                    <td className="px-5 py-2.5 text-xs font-mono text-slate-400">{evt.session_id.slice(0, 8)}</td>
                    <td className="px-5 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        evt.event_type === 'converted' ? 'bg-emerald-500/10 text-emerald-400'
                        : evt.event_type === 'presented' ? 'bg-blue-500/10 text-blue-400'
                        : evt.event_type === 'rejected' ? 'bg-rose-500/10 text-rose-400'
                        : 'bg-slate-800 text-slate-400'
                      }`}>{evt.event_type}</span>
                    </td>
                    <td className="px-5 py-2.5 text-slate-300">{evt.vertical_id || '--'}</td>
                    <td className="px-5 py-2.5 text-center text-slate-400">{evt.phase ?? '--'}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-slate-300">{evt.score != null ? evt.score.toFixed(1) : '--'}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-emerald-400">{evt.revenue != null ? `$${evt.revenue.toFixed(2)}` : '--'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════
//  LEADS TAB (preserved from original)
// ════════════════════════════════════════════════════════

interface LeadsTabProps {
  isLoading: boolean;
  kpis: {
    rpus: number; rpusChange: number;
    revenue: number; revenueChange: number;
    sessions: number; sessionsChange: number;
    cvr: number; cvrChange: number;
  };
  timeSeries: { label: string; leads: number; revenue: number }[];
  sourceBreakdown: { name: string; value: number }[];
  statusBreakdown: { name: string; leads: number }[];
  deliveryStats: { total: number; success: number; rate: number };
  currentLeads: Lead[];
  timeRange: string;
}

function LeadsTab({ isLoading, kpis, timeSeries, sourceBreakdown, statusBreakdown, deliveryStats, currentLeads, timeRange }: LeadsTabProps) {
  return (
    <>
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="RPUS" value={`$${kpis.rpus.toFixed(2)}`} change={kpis.rpusChange} icon={<DollarSign className="h-5 w-5" />} tint="emerald" />
        <KpiCard label="Revenue" value={`$${kpis.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} change={kpis.revenueChange} icon={<TrendingUp className="h-5 w-5" />} tint="blue" />
        <KpiCard label="Sessions" value={kpis.sessions.toLocaleString()} change={kpis.sessionsChange} icon={<Users className="h-5 w-5" />} tint="violet" />
        <KpiCard label="CVR" value={`${kpis.cvr.toFixed(1)}%`} change={kpis.cvrChange} icon={<Target className="h-5 w-5" />} tint="amber" />
      </div>

      {/* Main area chart */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">Revenue & Leads Over Time</h3>
            <p className="text-xs text-slate-500">Last {timeRange} days vs the previous {timeRange} days</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Routing Accuracy</div>
            <div className="text-lg font-bold text-emerald-400">{deliveryStats.rate.toFixed(1)}%</div>
            <div className="text-[10px] text-slate-500">{deliveryStats.success}/{deliveryStats.total} delivered</div>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timeSeries} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="leadGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }} labelStyle={{ color: '#e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue ($)" stroke="#10b981" fill="url(#revGradient)" strokeWidth={2} />
              <Area yAxisId="right" type="monotone" dataKey="leads" name="Leads" stroke="#3b82f6" fill="url(#leadGradient)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two-column: source pie + status bar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
          <h3 className="font-semibold mb-4">Lead Source Mix</h3>
          {sourceBreakdown.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-slate-500 text-sm">No data in this range</div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={sourceBreakdown} cx="50%" cy="50%" outerRadius={80} innerRadius={48} dataKey="value" label={(entry) => `${entry.name} (${entry.value})`} labelLine={false}>
                    {sourceBreakdown.map((_, i) => (
                      <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
          <h3 className="font-semibold mb-4">Lead Status Funnel</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusBreakdown} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} />
                <Bar dataKey="leads" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent leads table */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="font-semibold">Recent Leads</h3>
          <span className="text-xs text-slate-500">{currentLeads.length} total -- showing last 10</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">Date</th>
                <th className="text-left px-5 py-2.5 font-medium">Name</th>
                <th className="text-left px-5 py-2.5 font-medium">Email</th>
                <th className="text-left px-5 py-2.5 font-medium">Source</th>
                <th className="text-left px-5 py-2.5 font-medium">Status</th>
                <th className="text-right px-5 py-2.5 font-medium">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">Loading...</td></tr>
              ) : currentLeads.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">No leads in this range</td></tr>
              ) : (
                currentLeads.slice(0, 10).map((lead) => (
                  <tr key={lead.id} className="border-t border-slate-800 hover:bg-slate-800/30">
                    <td className="px-5 py-2.5 text-slate-400 text-xs">{format(new Date(lead.created_at), 'MMM d, HH:mm')}</td>
                    <td className="px-5 py-2.5">{lead.name || '--'}</td>
                    <td className="px-5 py-2.5 text-slate-400">{lead.email || '--'}</td>
                    <td className="px-5 py-2.5">
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-300">{lead.source || 'flow'}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        lead.status === 'converted' ? 'bg-emerald-500/10 text-emerald-400'
                        : lead.status === 'contacted' ? 'bg-blue-500/10 text-blue-400'
                        : lead.status === 'qualified' ? 'bg-violet-500/10 text-violet-400'
                        : 'bg-slate-800 text-slate-400'
                      }`}>{lead.status}</span>
                    </td>
                    <td className="px-5 py-2.5 text-right font-mono text-emerald-400">${leadRevenue(lead).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ── KPI card ─────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string;
  change: number;
  icon: React.ReactNode;
  tint: 'emerald' | 'blue' | 'violet' | 'amber';
  invertChange?: boolean; // true = lower is better (e.g. rejection rate)
}

function KpiCard({ label, value, change, icon, tint, invertChange }: KpiCardProps) {
  const positive = invertChange ? change <= 0 : change >= 0;
  const tintClasses: Record<KpiCardProps['tint'], string> = {
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    violet: 'bg-violet-500/10 border-violet-500/20 text-violet-400',
    amber: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  };

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${tintClasses[tint]}`}>
          {icon}
        </div>
        <div className={`flex items-center gap-1 text-xs font-medium ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
          {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {pct(change)}
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}
